import type { OperationalFact } from '../facts/operational-facts.ts';
import {
  alignCutoffToBusinessDate,
  alignIncompleteOperatingPeriodProgress,
  compareMetric,
  previousOperatingBusinessDate,
  shiftBusinessDate,
  type ComparisonValue,
} from './comparison-ranking.ts';
import {
  projectBusinessDateProgress,
  type BusinessDateProgressProjection,
  type ProductPerformanceProjection,
  type ProductPerformanceRow,
} from './reporting-projection.ts';
import {
  projectOperatingPeriodProgress,
  type OperatingPeriodProgressProjection,
} from './period-progress.ts';

export interface ProgressMetrics {
  readonly turnoverMinor: number;
  readonly orderCount: number;
  readonly aovMinor: number | null;
  readonly productPerformance: ProductPerformanceProjection;
}

export interface ProductTrendRow {
  readonly productId: string;
  readonly customerName: string;
  readonly operationsName: string;
  readonly currentQuantity: number;
  readonly baselineQuantity: number;
  readonly currentLineAmountMinor: number;
  readonly baselineLineAmountMinor: number;
  readonly currentQuantityRank: number | null;
  readonly baselineQuantityRank: number | null;
  readonly currentAmountRank: number | null;
  readonly baselineAmountRank: number | null;
  readonly quantityComparison: ComparisonValue;
  readonly amountComparison: ComparisonValue;
}

export interface ProductTrendProjection {
  readonly sourceSafety: 'established' | 'source_not_established';
  readonly rows: readonly ProductTrendRow[];
  readonly biggestRisers: readonly ProductTrendRow[];
  readonly biggestDecliners: readonly ProductTrendRow[];
  readonly newProductIds: readonly string[];
}

export interface MetricComparisonProjection {
  readonly turnover: ComparisonValue;
  readonly orders: ComparisonValue;
  readonly aov: ComparisonValue;
  readonly products: ProductTrendProjection;
}

export interface DashboardComparisonProjection {
  readonly previousOperatingDay: MetricComparisonProjection;
  readonly sameWeekdayLastWeek: MetricComparisonProjection;
}

export interface ResolvedDashboardComparisonProjection {
  readonly current: BusinessDateProgressProjection;
  readonly previousOperatingBusinessDate: string | null;
  readonly previousOperatingDayProgress: BusinessDateProgressProjection | null;
  readonly sameWeekdayLastWeekBusinessDate: string | null;
  readonly sameWeekdayLastWeekProgress: BusinessDateProgressProjection | null;
  readonly comparison: DashboardComparisonProjection;
}

export interface IncompletePeriodComparisonProjection {
  readonly current: OperatingPeriodProgressProjection;
  readonly baseline: OperatingPeriodProgressProjection | null;
  readonly comparison: MetricComparisonProjection;
}

const unavailableProducts = (): ProductTrendProjection => ({
  sourceSafety: 'source_not_established',
  rows: [],
  biggestRisers: [],
  biggestDecliners: [],
  newProductIds: [],
});

const byId = (rows: readonly ProductPerformanceRow[]): Map<string, ProductPerformanceRow> =>
  new Map(rows.map((row) => [row.productId, row]));

const compareProducts = (
  current: ProductPerformanceProjection,
  baseline: ProductPerformanceProjection | null,
): ProductTrendProjection => {
  if (!baseline || current.sourceSafety !== 'established' || baseline.sourceSafety !== 'established') return unavailableProducts();
  const currentQuantity = byId(current.ranking);
  const currentAmount = byId(current.amountRanking);
  const baselineQuantity = byId(baseline.ranking);
  const baselineAmount = byId(baseline.amountRanking);
  const ids = [...new Set([...currentQuantity.keys(), ...baselineQuantity.keys()])].sort();
  const rows = ids.map((productId): ProductTrendRow => {
    const cq = currentQuantity.get(productId);
    const ca = currentAmount.get(productId);
    const bq = baselineQuantity.get(productId);
    const ba = baselineAmount.get(productId);
    const label = cq ?? bq;
    if (!label) throw new Error('REPORTING_PRODUCT_TREND_LABEL_REQUIRED');
    return {
      productId,
      customerName: label.customerName,
      operationsName: label.operationsName,
      currentQuantity: cq?.quantity ?? 0,
      baselineQuantity: bq?.quantity ?? 0,
      currentLineAmountMinor: ca?.lineAmountMinor ?? 0,
      baselineLineAmountMinor: ba?.lineAmountMinor ?? 0,
      currentQuantityRank: cq?.rank ?? null,
      baselineQuantityRank: bq?.rank ?? null,
      currentAmountRank: ca?.rank ?? null,
      baselineAmountRank: ba?.rank ?? null,
      quantityComparison: compareMetric(cq?.quantity ?? 0, bq?.quantity ?? 0),
      amountComparison: compareMetric(ca?.lineAmountMinor ?? 0, ba?.lineAmountMinor ?? 0),
    };
  });
  const validPercentRows = rows.filter((row) => row.quantityComparison.kind === 'percent');
  const biggestRisers = [...validPercentRows]
    .filter((row) => row.quantityComparison.kind === 'percent' && row.quantityComparison.deltaPercent > 0)
    .sort((a, b) => (b.quantityComparison.kind === 'percent' ? b.quantityComparison.deltaPercent : 0) - (a.quantityComparison.kind === 'percent' ? a.quantityComparison.deltaPercent : 0) || a.productId.localeCompare(b.productId))
    .slice(0, 3);
  const biggestDecliners = [...validPercentRows]
    .filter((row) => row.quantityComparison.kind === 'percent' && row.quantityComparison.deltaPercent < 0)
    .sort((a, b) => (a.quantityComparison.kind === 'percent' ? a.quantityComparison.deltaPercent : 0) - (b.quantityComparison.kind === 'percent' ? b.quantityComparison.deltaPercent : 0) || a.productId.localeCompare(b.productId))
    .slice(0, 3);
  return {
    sourceSafety: 'established',
    rows,
    biggestRisers,
    biggestDecliners,
    newProductIds: rows.filter((row) => row.quantityComparison.kind === 'new').map((row) => row.productId),
  };
};

export const buildProgressComparison = (
  current: ProgressMetrics,
  baseline: ProgressMetrics | null,
): MetricComparisonProjection => ({
  turnover: compareMetric(current.turnoverMinor, baseline?.turnoverMinor ?? null),
  orders: compareMetric(current.orderCount, baseline?.orderCount ?? null),
  aov: current.aovMinor === null || baseline?.aovMinor == null
    ? { kind: 'not_available' }
    : compareMetric(current.aovMinor, baseline.aovMinor),
  products: compareProducts(current.productPerformance, baseline?.productPerformance ?? null),
});

export function buildDashboardComparison(input: {
  readonly current: ProgressMetrics;
  readonly previousOperatingDay: ProgressMetrics | null;
  readonly sameWeekdayLastWeek: ProgressMetrics | null;
}): DashboardComparisonProjection {
  return {
    previousOperatingDay: buildProgressComparison(input.current, input.previousOperatingDay),
    sameWeekdayLastWeek: buildProgressComparison(input.current, input.sameWeekdayLastWeek),
  };
}

export function buildDashboardComparisonFromFacts(input: {
  readonly facts: readonly OperationalFact[];
  readonly currentBusinessDate: string;
  readonly currentCutoffAt: string;
  readonly operatingBusinessDates: readonly string[];
}): ResolvedDashboardComparisonProjection {
  const current = projectBusinessDateProgress({
    facts: input.facts,
    businessDate: input.currentBusinessDate,
    cutoffAt: input.currentCutoffAt,
  });
  const previousDate = previousOperatingBusinessDate(input.currentBusinessDate, input.operatingBusinessDates);
  const operatingDates = new Set(input.operatingBusinessDates);
  const sameWeekdayCandidate = shiftBusinessDate(input.currentBusinessDate, -7);
  const sameWeekdayDate = operatingDates.has(sameWeekdayCandidate) ? sameWeekdayCandidate : null;
  const previousProgress = previousDate
    ? projectBusinessDateProgress({
        facts: input.facts,
        businessDate: previousDate,
        cutoffAt: alignCutoffToBusinessDate(input.currentCutoffAt, previousDate),
      })
    : null;
  const sameWeekdayProgress = sameWeekdayDate
    ? projectBusinessDateProgress({
        facts: input.facts,
        businessDate: sameWeekdayDate,
        cutoffAt: alignCutoffToBusinessDate(input.currentCutoffAt, sameWeekdayDate),
      })
    : null;
  return {
    current,
    previousOperatingBusinessDate: previousDate,
    previousOperatingDayProgress: previousProgress,
    sameWeekdayLastWeekBusinessDate: sameWeekdayDate,
    sameWeekdayLastWeekProgress: sameWeekdayProgress,
    comparison: buildDashboardComparison({
      current,
      previousOperatingDay: previousProgress,
      sameWeekdayLastWeek: sameWeekdayProgress,
    }),
  };
}

export function buildIncompletePeriodComparisonFromFacts(input: {
  readonly facts: readonly OperationalFact[];
  readonly currentBusinessDates: readonly string[];
  readonly baselineBusinessDates: readonly string[];
  readonly currentCutoffAt: string;
}): IncompletePeriodComparisonProjection {
  const alignment = alignIncompleteOperatingPeriodProgress(
    input.currentBusinessDates,
    input.baselineBusinessDates,
    input.currentCutoffAt,
  );
  const currentDates = alignment?.currentBusinessDates ?? [...new Set(input.currentBusinessDates)].sort();
  const current = projectOperatingPeriodProgress({
    facts: input.facts,
    businessDates: currentDates,
    cutoffAt: input.currentCutoffAt,
  });
  if (!alignment) {
    return { current, baseline: null, comparison: buildProgressComparison(current, null) };
  }
  const baseline = projectOperatingPeriodProgress({
    facts: input.facts,
    businessDates: alignment.baselineBusinessDates,
    cutoffAt: alignment.baselineCutoffAt,
  });
  return { current, baseline, comparison: buildProgressComparison(current, baseline) };
}
